import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState } from 'react';

import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SingleSelect from '../../../../../../components/SingleSelect';
import BasicDateTimePicker from '../../../../../../components/SelectDateTime';
import HttpStatus from '../../../../../../enums/httpStatus';
import { customersApi, visitRoutineAPI } from '../../../../../../services/requests';
import { SellerVisitRoutine } from '../../../../../../types';
import { getOptionsFromCustomers } from '../../../../../../utils/getSelectOptions';
import {
  frequencyIndex,
  getFrequencyOptions,
  getVisitRoutineWeekDayOptions,
  visitRoutineWeekDays,
} from '../../../../../../utils/helpers';
import { DateFrequency } from '../../../../../../utils/enums';

import SelectAsyncJSX from '../../../../../../components/SelectAsync';

const parseDateTimeValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  return dayjs(value.replace(' ', 'T'));
};

type Props = {
  closeModal: () => void;
  selecteditem: SellerVisitRoutine;
  reqCallback: () => void;
};

type Option = {
  value: any;
  label: string;
};

const ModalEditRoutine = ({ closeModal, selecteditem, reqCallback }: Props) => {
  const [waitingRequest, setWaitingRequest] = useState(false);

  const [weekDay, setWeekDay] = useState<Option | Record<string, unknown>>({
    label: visitRoutineWeekDays()[selecteditem.DiaSemana] ?? selecteditem.DiaSemana,
    value: selecteditem.DiaSemana,
  });

  const [frequency, setFrequency] = useState<Option | Record<string, unknown>>({
    label: frequencyIndex()[selecteditem.Periodicidade],
    value: selecteditem.Periodicidade,
  });
  const isDailyFrequency = Number(frequency.value) === DateFrequency.daily;

  const [startDate, setStartDate] = useState<any>(parseDateTimeValue(selecteditem.start_date));
  const [salesTargetValue] = useState(selecteditem.VlrMetaVenda);
  const [note] = useState(selecteditem.Obs);

  const [customer, setCustomer] = useState<Option | Record<string, unknown>>({
    label: `${selecteditem.customer?.CodigoCliente} - ${selecteditem.customer?.RazaoSocial}`,
    value: selecteditem.CodigoCliente,
  });

  const reqEdit = async () => {
    setWaitingRequest(true);
    try {
      const data = {
        CodigoVendedor: selecteditem.CodigoVendedor,
        CodigoCliente: customer.value,
        Periodicidade: frequency.value.toString(),
        DiaSemana: String(weekDay.value),
        start_date: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss'),
        VlrMetaVenda: salesTargetValue,
        Obs: note,
      };

      const res = await visitRoutineAPI.update(data, selecteditem.id);

      if (res.status === HttpStatus.OK) {
        await reqCallback();
        frontendNotification({
          message: t('successfullyUpdated'),
          type: 'success',
        });
        closeModal();
      } else {
        throw res;
      }
    } catch (err) {
      if (!visitRoutineAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }
    setWaitingRequest(false);
  };

  return (
    <Modal
      title={t('visitUpdate')}
      action={() => reqEdit()}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      actionNameBtn={t('save')}
      isLoading={waitingRequest}
      style={{ minWidth: 'max-content' }}
      bodyStyle={{ minWidth: '100%' }}
    >
      <div>
        <div className="row">
          <div className="itemFormWithLabel" style={{ maxWidth: '20rem' }}>
            <div className="label">{t('seller') as string}</div>
            {/* eslint-disable-next-line max-len */}
            <p>{`${selecteditem.seller?.CodigoVendedor} - ${selecteditem.seller?.Nome}`}</p>
          </div>
          <div className="itemFormWithLabel" style={{ maxWidth: '12rem' }}>
            <div className="label">{t('frequency') as string}</div>
            <SingleSelect
              value={frequency}
              onChange={(e: any) => setFrequency(e)}
              options={getFrequencyOptions({ includeAlways: false })}
            />
          </div>

          <div className="itemFormWithLabel" style={{ maxWidth: '12rem' }}>
            <div className="label">{t('weekDay') as string}</div>
            <SingleSelect
              value={weekDay}
              onChange={(e: any) => setWeekDay(e)}
              options={getVisitRoutineWeekDayOptions()}
              disabled={isDailyFrequency}
            />
          </div>

          <div className="itemFormWithLabel" style={{ maxWidth: '22rem' }}>
            <div className="label">{t('routineStartDate') as string}</div>
            <BasicDateTimePicker
              value={startDate}
              onChange={setStartDate}
              disabled={waitingRequest}
              disablePast={false}
              label=""
            />
          </div>

          <div className="itemFormWithLabel" style={{ maxWidth: '20rem' }}>
            <div className="label">{t('selectCustomer') as string}</div>
            <SelectAsyncJSX
              value={customer}
              onChange={(e: any) => setCustomer(e)}
              request={customersApi}
              reqResponseToOption={getOptionsFromCustomers}
              query={`sellerCode=${selecteditem.CodigoVendedor}`}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalEditRoutine;
